// GL6 (#6) — "Personel oluştur" form çekmecesi (EP + SP ortak). api-contract GL6 §4.2.
// Tek çağrı: T.C. + erişim seviyesi zorunlu kişi kaydı; isteğe bağlı iletişim YALNIZ aktivasyon
// bağlantısı içindir ve kişinin kaydına yazılmaz (#29). Sheet + sabit footer (`tile`), `useDiscardGuard`,
// #29'un ad/T.C. doğrulayıcıları ve `personnelForm.*` alan metinleri yeniden kullanılır.
//
// BAŞARI METNİ yalnız `result.link !== null`'dan seçilir. `result.personnel.kind` bu dosyada HİÇ okunmaz:
// sunucu iletişim başka bir hesaba aitse kişi yazmadan düz davet gönderir (D1) ve yanıt aynı görünmelidir.
// T.C. başarıda formdan silinir ve sonuç adımında asla gösterilmez (EP yetkisiz kullanıcı için yanıt da maskelidir).
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { UserPlus } from 'lucide-react';
import { type RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
  type CreatePersonnelRequest,
  type CreatePersonnelResult,
  toInviteContact,
} from '../api/contractors';
import { hasErrorCode, isApiError } from '../api/errors';
import { resolveApiErrorMessage } from '../i18n/apiError';
import { Alert } from '../ui/Alert';
import { Button } from '../ui/Button';
import { Field } from '../ui/Field';
import { FormSection } from '../ui/FormSection';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Sheet } from '../ui/Sheet';
import { Spinner } from '../ui/Spinner';
import { useDiscardGuard } from '../ui/useDiscardGuard';
import { ActivationContactFields } from './ActivationContactFields';
import { ActivationLinkResultPanel } from './ActivationLinkResultPanel';
import type { PersonnelActivationClient } from './activationClient';
import { isLinkRateLimit, resolveActivationLinkError } from './activationLinkErrors';
import {
  addContactIssues,
  bothContactsFilled,
  createContactIssues,
  inviteServerFieldErrors,
  isChannelUnavailable,
} from './personnelContact';
import { isValidTcKimlikNo } from './tcKimlik';
import { NAME_MAX, nameSchema } from './usePersonnelEditor';

export interface PersonnelCreateSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: PersonnelActivationClient;
  onCreated?: (result: CreatePersonnelResult) => void;
  restoreFocusRef: RefObject<HTMLElement | null>;
}

function buildSchema(t: TFunction) {
  return z
    .object({
      firstName: nameSchema(
        true,
        t('personnelForm.validation.firstNameRequired'),
        t('personnelForm.validation.firstNameTooLong', { max: NAME_MAX }),
      ),
      lastName: nameSchema(
        true,
        t('personnelForm.validation.lastNameRequired'),
        t('personnelForm.validation.lastNameTooLong', { max: NAME_MAX }),
      ),
      tcKimlikNo: z
        .string()
        .trim()
        .min(1, t('personnelCreate.validation.tcRequired'))
        .refine((v) => isValidTcKimlikNo(v), t('personnelForm.validation.tcInvalid')),
      accessLevel: z.enum(['Personeli', 'Yetkili']),
      email: z.string(),
      phone: z.string(),
      channel: z.enum(['Email', 'Sms', 'Both']),
    })
    .superRefine((values, ctx) => {
      addContactIssues(ctx, createContactIssues(values.email, values.phone), t);
    });
}
type FormValues = z.infer<ReturnType<typeof buildSchema>>;
type FieldName = 'firstName' | 'lastName' | 'tcKimlikNo' | 'email' | 'phone';

const DEFAULTS: FormValues = {
  firstName: '',
  lastName: '',
  tcKimlikNo: '',
  accessLevel: 'Personeli',
  email: '',
  phone: '',
  channel: 'Both',
};

function toRequest(values: FormValues): CreatePersonnelRequest {
  const contact = toInviteContact(values.email, values.phone, values.channel);
  return {
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    tcKimlikNo: values.tcKimlikNo.trim(),
    accessLevel: values.accessLevel,
    email: contact?.email ?? null,
    phone: contact?.phone ?? null,
    // Kanal YALNIZ iki iletişim birlikte doluyken gider (`toInviteContact`).
    ...(contact?.channel ? { channel: contact.channel } : {}),
  };
}

/** Alana bağlanan sunucu hataları. Create `GUEST_INVITE_CONTACT_ALREADY_EXISTS` dönmez (D-a); dönerse yine nötr alan metni (D1). */
function serverFieldErrors(
  error: unknown,
  t: TFunction,
): Array<{ field: FieldName; message: string }> {
  const out: Array<{ field: FieldName; message: string }> = [];
  if (hasErrorCode(error, 'TC_KIMLIK_INVALID')) {
    out.push({
      field: 'tcKimlikNo',
      message: resolveApiErrorMessage(error, t, 'personnelForm.validation.tcInvalid'),
    });
  } else if (hasErrorCode(error, 'PERSONNEL_TC_ALREADY_EXISTS')) {
    out.push({
      field: 'tcKimlikNo',
      message: resolveApiErrorMessage(error, t, 'personnelForm.validation.tcExistsFallback'),
    });
  } else if (isApiError(error) && error.errorCode === 'VALIDATION_FAILED') {
    for (const item of error.errors) {
      const name = (item.field ?? '').toLowerCase();
      const empty = item.code === 'FIELD_EMPTY';
      if (name === 'firstname') {
        out.push({
          field: 'firstName',
          message: t(
            empty
              ? 'personnelForm.validation.firstNameRequired'
              : 'personnelForm.validation.firstNameTooLong',
            { max: NAME_MAX },
          ),
        });
      } else if (name === 'lastname') {
        out.push({
          field: 'lastName',
          message: t(
            empty
              ? 'personnelForm.validation.lastNameRequired'
              : 'personnelForm.validation.lastNameTooLong',
            { max: NAME_MAX },
          ),
        });
      } else if (name === 'tckimlikno') {
        out.push({
          field: 'tcKimlikNo',
          message: t(
            empty ? 'personnelCreate.validation.tcRequired' : 'personnelForm.validation.tcInvalid',
          ),
        });
      }
    }
    out.push(...inviteServerFieldErrors(error, t));
  }
  return out;
}

export function PersonnelCreateSheet({
  open,
  onOpenChange,
  client,
  onCreated,
  restoreFocusRef,
}: PersonnelCreateSheetProps) {
  const { t } = useTranslation();
  const [result, setResult] = useState<CreatePersonnelResult | null>(null);
  const inflightRef = useRef(false);
  const resultRef = useRef<HTMLDivElement>(null);
  const focusFirstRef = useRef(false);

  const schema = useMemo(() => buildSchema(t), [t]);
  const { register, handleSubmit, reset, watch, setError, setFocus, formState } =
    useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: DEFAULTS });

  const mutation = useMutation({
    mutationFn: (values: FormValues) => client.create(toRequest(values)),
    onSuccess: (created) => {
      client.invalidate();
      setResult(created);
      reset(DEFAULTS); // T.C. ve iletişim bellekten düşer
      onCreated?.(created);
    },
    onError: (error, values) => {
      const fieldErrors = serverFieldErrors(error, t);
      for (const fieldError of fieldErrors) {
        setError(fieldError.field, { type: 'server', message: fieldError.message });
      }
      const sentBoth = bothContactsFilled(values.email, values.phone);
      const first = fieldErrors[0];
      if (first) {
        setFocus(first.field);
      } else if (isChannelUnavailable(error, sentBoth)) {
        setFocus('channel');
      }
    },
    onSettled: () => {
      inflightRef.current = false;
    },
  });
  const { reset: resetMutation } = mutation;

  const submitting = mutation.isPending;
  const [firstName, lastName, tcKimlikNo, email, phone] = watch([
    'firstName',
    'lastName',
    'tcKimlikNo',
    'email',
    'phone',
  ]);
  const bothContacts = bothContactsFilled(email, phone);
  // "Yazılmış girdi" — RHF `isDirty` KULLANILMAZ (gizlenen kanalın eski değeri formu dirty bırakır).
  const hasTypedInput =
    result === null && [firstName, lastName, tcKimlikNo, email, phone].some((v) => v.trim() !== '');

  const hasFieldErrors = mutation.isError && serverFieldErrors(mutation.error, t).length > 0;
  const channelError = isChannelUnavailable(mutation.error, bothContacts)
    ? resolveApiErrorMessage(mutation.error, t)
    : null;
  // R3/R4: iki 429'un sebebi (iletişim) ve çaresi (iletişimsiz oluştur) iletişim grubundadır → orada gösterilir.
  // Diğer her alan-dışı hata (403, rol yapılandırılmamış, ağ) üstte bannerdadır.
  const rateLimited = mutation.isError && isLinkRateLimit(mutation.error);
  const contactGroupError = rateLimited
    ? resolveActivationLinkError(mutation.error, t, { create: true })
    : null;
  const bannerError =
    mutation.isError && !rateLimited && !hasFieldErrors && channelError === null
      ? resolveActivationLinkError(mutation.error, t, { create: true })
      : null;
  const bannerRef = useRef<HTMLDivElement>(null);
  const contactErrorRef = useRef<HTMLDivElement>(null);
  // R3: 1440×900'de form (≈586–704px) gövdeden (≈615px) uzundur ve gönder düğmesi altta sabittir;
  // üstteki banner ekran dışında kalırdı. Görünür yap ve odağı ona taşı (role="status" tek başına duyurmaz).
  useEffect(() => {
    const target = bannerError
      ? bannerRef.current
      : contactGroupError
        ? contactErrorRef.current
        : null;
    if (target) {
      target.scrollIntoView({ block: 'nearest' });
      target.focus();
    }
  }, [bannerError, contactGroupError]);

  // Başarıda form unmount olur → odak <body>'ye düşerdi; sonuca taşı.
  useEffect(() => {
    if (result) {
      resultRef.current?.focus();
    }
  }, [result]);
  // "Yeni personel oluştur" sonrası form yeniden mount olur → ilk alana odak.
  useEffect(() => {
    if (!result && focusFirstRef.current) {
      focusFirstRef.current = false;
      setFocus('firstName');
    }
  }, [result, setFocus]);

  function resetAll() {
    reset(DEFAULTS);
    setResult(null);
    resetMutation();
    inflightRef.current = false;
  }
  function closeNow() {
    resetAll();
    onOpenChange(false);
  }
  const { requestClose, discardDialog } = useDiscardGuard({
    dirty: hasTypedInput,
    busy: submitting,
    onClose: closeNow,
  });
  function handleOpenChange(next: boolean) {
    if (next) {
      onOpenChange(true);
      return;
    }
    requestClose();
  }

  const successKey =
    result?.link != null ? 'personnelCreate.success.linkSent' : 'personnelCreate.success.created';

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={handleOpenChange}
        busy={submitting}
        tile
        title={t('personnelCreate.title')}
        description={result ? undefined : t('personnelCreate.description')}
        closeLabel={t('common.close')}
        restoreFocusRef={restoreFocusRef}
        footer={
          result ? (
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  focusFirstRef.current = true;
                  resetAll();
                }}
              >
                <UserPlus className="h-4 w-4" aria-hidden="true" />
                {t('personnelCreate.another')}
              </Button>
              <Button onClick={() => handleOpenChange(false)}>{t('common.close')}</Button>
            </>
          ) : (
            <>
              <Button
                variant="secondary"
                onClick={() => handleOpenChange(false)}
                disabled={submitting}
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit" form="personnel-create-form" loading={submitting}>
                {submitting ? (
                  <Spinner label={t('personnelCreate.submit')} />
                ) : (
                  t('personnelCreate.submit')
                )}
              </Button>
            </>
          )
        }
      >
        {result ? (
          <div
            ref={resultRef}
            tabIndex={-1}
            // biome-ignore lint/a11y/useSemanticElements: programatik odak hedefi; <fieldset> kenarlık/min-width sıfırlaması gerektirir
            role="group"
            aria-labelledby="personnel-create-result-hint"
            className="flex flex-col gap-4 focus:outline-none"
          >
            <Alert tone="success">
              <span id="personnel-create-result-hint">{t(successKey)}</span>
            </Alert>
            {result.link ? <ActivationLinkResultPanel result={result.link} /> : null}
          </div>
        ) : (
          <form
            id="personnel-create-form"
            className="flex flex-col gap-4"
            noValidate
            onSubmit={handleSubmit((values) => {
              // Eşzamanlı çift Enter: RHF doğrulaması async, `isPending` yeniden render'a kadar false kalır.
              if (inflightRef.current) {
                return;
              }
              inflightRef.current = true;
              mutation.mutate(values);
            })}
          >
            {bannerError ? (
              <div ref={bannerRef} tabIndex={-1} className="focus:outline-none">
                <Alert tone="danger">{bannerError}</Alert>
              </div>
            ) : null}

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                htmlFor="personnel-create-firstName"
                label={t('personnelForm.field.firstName')}
                error={formState.errors.firstName?.message}
                required
              >
                <Input
                  id="personnel-create-firstName"
                  autoComplete="off"
                  invalid={Boolean(formState.errors.firstName)}
                  aria-describedby={
                    formState.errors.firstName ? 'personnel-create-firstName-error' : undefined
                  }
                  {...register('firstName')}
                />
              </Field>
              <Field
                htmlFor="personnel-create-lastName"
                label={t('personnelForm.field.lastName')}
                error={formState.errors.lastName?.message}
                required
              >
                <Input
                  id="personnel-create-lastName"
                  autoComplete="off"
                  invalid={Boolean(formState.errors.lastName)}
                  aria-describedby={
                    formState.errors.lastName ? 'personnel-create-lastName-error' : undefined
                  }
                  {...register('lastName')}
                />
              </Field>
            </div>

            <Field
              htmlFor="personnel-create-tc"
              label={t('personnelForm.field.tcKimlikNo')}
              hint={t('personnelForm.field.tcKimlikNoHint')}
              error={formState.errors.tcKimlikNo?.message}
              required
            >
              <Input
                id="personnel-create-tc"
                inputMode="numeric"
                maxLength={11}
                autoComplete="off"
                invalid={Boolean(formState.errors.tcKimlikNo)}
                aria-describedby={
                  formState.errors.tcKimlikNo
                    ? 'personnel-create-tc-error'
                    : 'personnel-create-tc-hint'
                }
                {...register('tcKimlikNo')}
              />
            </Field>

            <Field
              htmlFor="personnel-create-access"
              label={t('personnelForm.field.accessLevel')}
              hint={t('personnelForm.field.accessHint')}
              required
            >
              <Select
                id="personnel-create-access"
                aria-describedby="personnel-create-access-hint"
                options={[
                  { value: 'Personeli', label: t('personnelForm.access.personeli') },
                  { value: 'Yetkili', label: t('personnelForm.access.yetkili') },
                ]}
                {...register('accessLevel')}
              />
            </Field>

            {/* R5: gerçek <fieldset>/<legend> (`FormSection`); kutu emsali `CheckInModal` ("Onaylar" bloğu): yalnız px-/pb-. */}
            <FormSection
              legend={t('personnelCreate.contact.title')}
              className="rounded-md border border-border bg-surface-muted/40 px-4 pb-4"
              legendClassName="px-1"
            >
              <p className="text-xs text-muted">{t('personnelCreate.contact.hint')}</p>
              {contactGroupError ? (
                <div ref={contactErrorRef} tabIndex={-1} className="focus:outline-none">
                  <Alert tone="warning">{contactGroupError}</Alert>
                </div>
              ) : null}
              {/* `deps`: biri değişince öbürü de yeniden doğrulanır. */}
              <ActivationContactFields
                idPrefix="personnel-create"
                emailProps={register('email', { deps: ['phone'] })}
                phoneProps={register('phone', { deps: ['email'] })}
                channelProps={register('channel')}
                emailError={formState.errors.email?.message}
                phoneError={formState.errors.phone?.message}
                channelError={channelError}
                showChannel={bothContacts}
              />
            </FormSection>
          </form>
        )}
      </Sheet>
      {discardDialog}
    </>
  );
}
